import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Toaster, toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import FileUpload from './components/FileUpload';
import AnalysisResults from './components/AnalysisResults';
import AIReportGenerator from './components/AIReportGenerator';
import Documentation from './components/Documentation';
import Support from './components/Support';
import Header from './components/Header';
import LoadingSpinner from './components/LoadingSpinner';
import './App.css';

// Interface pour le rapport IA
interface AIReport {
  mainReport: string;
  detailedInsights?: string;
  generatedAt: string;
  dataSource: {
    filename: string;
    period: string;
    monthsAnalyzed: number;
  };
}

interface AnalysisData {
  success: boolean;
  filename: string;
  sheets: string[];
  analysis: {
    monthlyData: any[];
    weeklyData: any[];
    objectives: any[];
    comparisons: any[];
    kpis: {
      // Année N-1 - Annuel
      totalRevenueAnnualPrevious: string;
      totalFreqAnnualPrevious: string;
      avgPMAnnualPrevious: string;
      
      // Année N-1 - À date
      totalRevenueToDatePrevious: string;
      totalFreqToDatePrevious: string;
      avgPMToDatePrevious: string;
      avgMonthlyToDatePrevious: string;
      
      // Année N - Annuel
      totalRevenueAnnualCurrent: string;
      totalFreqAnnualCurrent: string;
      avgPMAnnualCurrent: string;
      
      // Année N - À date
      totalRevenueToDateCurrent: string;
      totalFreqToDateCurrent: string;
      avgPMToDateCurrent: string;
      avgMonthlyToDateCurrent: string;
      
      // Objectifs
      totalObjectivesToDate: string;
      totalObjectivesAnnual: string;
      objectiveRealizationToDate: string;
      objectiveRealizationAnnual: string;
      
      // Objectifs par métrique
      totalCAObjectivesAnnual: string;
      totalFreqObjectivesAnnual: string;
      totalPMObjectivesAnnual: string;
      
      // Comparaisons à date
      revenueGrowthToDate: string;
      freqGrowthToDate: string;
      pmGrowthToDate: string;
      
      // Comparaisons annuelles
      revenueGrowthAnnual: string;
      freqGrowthAnnual: string;
      pmGrowthAnnual: string;
      
      // Métadonnées
      yearPrevious: number;
      yearCurrent: number;
      currentMonth: number;
      currentMonthName: string;
    };
    summary: string;
    currentMonth: number;
    availableMetrics: string[];
  };
  rawData: {
    headers: string[];
    rowCount: number;
  };
}

function App() {
  const [analysisData, setAnalysisData] = useState<AnalysisData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<'upload' | 'analysis' | 'report'>('upload');
  const [currentPage, setCurrentPage] = useState<'analysis' | 'documentation' | 'support'>('analysis');
  const [isDataRestored, setIsDataRestored] = useState(false);
  
  // État pour le rapport IA
  const [aiReport, setAiReport] = useState<AIReport | null>(null);
  
  // Ref pour éviter les doubles notifications en mode développement
  const hasRestoredData = useRef(false);

  // Clés pour le localStorage
  const STORAGE_KEY = 'paries-excel-analysis';
  const STORAGE_TIMESTAMP_KEY = 'paries-excel-analysis-timestamp';
  const REPORT_STORAGE_KEY = 'paries-excel-ai-report';
  const REPORT_TIMESTAMP_KEY = 'paries-excel-ai-report-timestamp';
  const EXPIRATION_HOURS = 24; // Expiration après 24 heures

  // Fonction pour nettoyer les données expirées
  const cleanExpiredData = useCallback(() => {
    try {
      const savedTimestamp = localStorage.getItem(STORAGE_TIMESTAMP_KEY);
      const reportTimestamp = localStorage.getItem(REPORT_TIMESTAMP_KEY);
      let dataExpired = false;
      let reportExpired = false;
      
      // Vérifier les données d'analyse
      if (savedTimestamp) {
        const timestamp = parseInt(savedTimestamp);
        const now = Date.now();
        const hoursElapsed = (now - timestamp) / (1000 * 60 * 60);
        
        if (hoursElapsed >= EXPIRATION_HOURS) {
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(STORAGE_TIMESTAMP_KEY);
          dataExpired = true;
        }
      }
      
      // Vérifier le rapport IA
      if (reportTimestamp) {
        const timestamp = parseInt(reportTimestamp);
        const now = Date.now();
        const hoursElapsed = (now - timestamp) / (1000 * 60 * 60);
        
        if (hoursElapsed >= EXPIRATION_HOURS) {
          localStorage.removeItem(REPORT_STORAGE_KEY);
          localStorage.removeItem(REPORT_TIMESTAMP_KEY);
          reportExpired = true;
        }
      }
      
      return { dataExpired, reportExpired };
    } catch (error) {
      console.error('Erreur lors du nettoyage:', error);
      // En cas d'erreur, supprimer toutes les données par sécurité
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(STORAGE_TIMESTAMP_KEY);
      localStorage.removeItem(REPORT_STORAGE_KEY);
      localStorage.removeItem(REPORT_TIMESTAMP_KEY);
      return { dataExpired: true, reportExpired: true };
    }
  }, []);

  // Charger les données depuis le localStorage au démarrage
  useEffect(() => {
    // Éviter les doubles exécutions en mode développement
    if (hasRestoredData.current) return;
    
    const savedData = localStorage.getItem(STORAGE_KEY);
    const savedTimestamp = localStorage.getItem(STORAGE_TIMESTAMP_KEY);
    const savedReport = localStorage.getItem(REPORT_STORAGE_KEY);
    const savedReportTimestamp = localStorage.getItem(REPORT_TIMESTAMP_KEY);
    
    // Nettoyer les données expirées
    const { dataExpired, reportExpired } = cleanExpiredData();
    
    // Restaurer les données d'analyse si disponibles
    if (savedData && savedTimestamp && !dataExpired) {
      try {
        const parsedData: AnalysisData = JSON.parse(savedData);
        setAnalysisData(parsedData);
        setCurrentStep('analysis');
        setIsDataRestored(true);
        hasRestoredData.current = true;
        
        const timestamp = parseInt(savedTimestamp);
        const now = Date.now();
        const hoursElapsed = (now - timestamp) / (1000 * 60 * 60);
        const timeLeft = Math.round(EXPIRATION_HOURS - hoursElapsed);
        
        toast.success(`Analyse précédente restaurée ! (expire dans ${timeLeft}h)`, {
          duration: 4000,
          icon: '🔄',
        });
      } catch (error) {
        console.error('Erreur lors du chargement des données sauvegardées:', error);
        hasRestoredData.current = true;
        toast.error('Erreur lors du chargement des données sauvegardées', {
          duration: 3000,
          icon: '❌',
        });
      }
    }
    
    // Restaurer le rapport IA si disponible
    if (savedReport && savedReportTimestamp && !reportExpired) {
      try {
        const parsedReport: AIReport = JSON.parse(savedReport);
        setAiReport(parsedReport);
        
        console.log('📄 Rapport IA restauré depuis le cache');
        toast.success('Rapport IA restauré !', {
          duration: 2000,
          icon: '🤖',
        });
      } catch (error) {
        console.error('Erreur lors du chargement du rapport IA:', error);
        localStorage.removeItem(REPORT_STORAGE_KEY);
        localStorage.removeItem(REPORT_TIMESTAMP_KEY);
      }
    }
    
    // Notifications d'expiration
    if (dataExpired && !hasRestoredData.current) {
      toast.error('Données sauvegardées expirées (>24h)', {
        duration: 3000,
        icon: '⏰',
      });
    }
    
    if (reportExpired) {
      console.log('⏰ Rapport IA expiré et supprimé');
    }
    
    if (!hasRestoredData.current) {
      hasRestoredData.current = true;
    }
  }, [cleanExpiredData]);

  // Sauvegarder les données dans le localStorage
  const saveToLocalStorage = useCallback((data: AnalysisData) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      localStorage.setItem(STORAGE_TIMESTAMP_KEY, Date.now().toString());
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde des données');
    }
  }, []);

  // Sauvegarder le rapport IA
  const saveReportToLocalStorage = useCallback((report: AIReport) => {
    try {
      localStorage.setItem(REPORT_STORAGE_KEY, JSON.stringify(report));
      localStorage.setItem(REPORT_TIMESTAMP_KEY, Date.now().toString());
      console.log('💾 Rapport IA sauvegardé en cache');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du rapport:', error);
      toast.error('Erreur lors de la sauvegarde du rapport');
    }
  }, []);

  const handleFileUpload = useCallback(async (file: File) => {
    setIsLoading(true);
    const formData = new FormData();
    formData.append('excelFile', file);

    try {
      const response = await fetch('/api/upload-excel', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur lors de l\'upload');
      }

      const data: AnalysisData = await response.json();
      setAnalysisData(data);
      setCurrentStep('analysis');
      setIsDataRestored(false);
      
      // Réinitialiser le rapport IA car nouvelles données
      setAiReport(null);
      localStorage.removeItem(REPORT_STORAGE_KEY);
      localStorage.removeItem(REPORT_TIMESTAMP_KEY);
      
      // Sauvegarder automatiquement les nouvelles données
      saveToLocalStorage(data);
      
      toast.success(`Fichier ${file.name} analysé avec succès !`, {
        duration: 4000,
        icon: '📊',
      });

    } catch (error) {
      console.error('Erreur upload:', error);
      toast.error(error instanceof Error ? error.message : 'Erreur lors de l\'upload', {
        duration: 6000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [saveToLocalStorage]);

  const handleNewAnalysis = useCallback(() => {
    // Supprimer toutes les données sauvegardées
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_TIMESTAMP_KEY);
    localStorage.removeItem(REPORT_STORAGE_KEY);
    localStorage.removeItem(REPORT_TIMESTAMP_KEY);
    setAnalysisData(null);
    setAiReport(null);
    setCurrentStep('upload');
    setIsDataRestored(false);
    hasRestoredData.current = false; // Reset pour permettre une nouvelle restauration
    toast.success('Prêt pour une nouvelle analyse !', {
      duration: 2000,
      icon: '🆕',
    });
  }, []);

  const handleGenerateReport = useCallback(() => {
    setCurrentStep('report');
  }, []);

  const handleBackToAnalysis = useCallback(() => {
    setCurrentStep('analysis');
  }, []);

  const handlePageChange = useCallback((page: 'analysis' | 'documentation' | 'support') => {
    setCurrentPage(page);
    // Si on va vers l'analyse et qu'on n'a pas de données, retourner à l'upload
    if (page === 'analysis' && !analysisData) {
      setCurrentStep('upload');
    }
  }, [analysisData]);

  // Callback pour sauvegarder le rapport généré
  const handleReportGenerated = useCallback((report: AIReport) => {
    setAiReport(report);
    saveReportToLocalStorage(report);
  }, [saveReportToLocalStorage]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
        }}
      />
      
      <Header currentPage={currentPage} onPageChange={handlePageChange} />
      
      <main className="container mx-auto px-4 py-8">
        <AnimatePresence mode="wait">
          {/* Page Documentation */}
          {currentPage === 'documentation' && (
            <motion.div
              key="documentation"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              <Documentation />
            </motion.div>
          )}

          {/* Page Support */}
          {currentPage === 'support' && (
            <motion.div
              key="support"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
            >
              <Support />
            </motion.div>
          )}

          {/* Page Analysis */}
          {currentPage === 'analysis' && (
            <>
              {isLoading && (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="flex justify-center items-center h-64"
                >
                  <LoadingSpinner />
                </motion.div>
              )}

              {!isLoading && currentStep === 'upload' && (
                <motion.div
                  key="upload"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-gray-800 mb-4">
                      Analyseur Financier Excel
                    </h1>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                      Uploadez votre fichier Excel contenant vos données financières 
                      pour obtenir une analyse complète avec graphiques et rapport automatisé.
                    </p>
                  </div>
                  
                  <FileUpload onFileUpload={handleFileUpload} />
                  
                  <div className="mt-12 text-center">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-6">
                      Fonctionnalités
                    </h2>
                    <div className="grid md:grid-cols-3 gap-6">
                      <div className="bg-white p-6 rounded-lg shadow-md">
                        <div className="text-3xl mb-4">📊</div>
                        <h3 className="font-semibold mb-2">Graphiques Dynamiques</h3>
                        <p className="text-gray-600">
                          Visualisations interactives de vos données mensuelles et hebdomadaires
                        </p>
                      </div>
                      <div className="bg-white p-6 rounded-lg shadow-md">
                        <div className="text-3xl mb-4">🤖</div>
                        <h3 className="font-semibold mb-2">Rapport IA</h3>
                        <p className="text-gray-600">
                          Analyse textuelle automatisée avec recommandations stratégiques
                        </p>
                      </div>
                      <div className="bg-white p-6 rounded-lg shadow-md">
                        <div className="text-3xl mb-4">📋</div>
                        <h3 className="font-semibold mb-2">Export PDF</h3>
                        <p className="text-gray-600">
                          Exportez vos analyses et rapports au format PDF
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {!isLoading && currentStep === 'analysis' && analysisData && (
                <motion.div
                  key="analysis"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                >
                  <AnalysisResults 
                    data={analysisData}
                    onNewAnalysis={handleNewAnalysis}
                    onGenerateReport={handleGenerateReport}
                    isDataRestored={isDataRestored}
                  />
                </motion.div>
              )}

              {!isLoading && currentStep === 'report' && analysisData && (
                <motion.div
                  key="report"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                >
                  <AIReportGenerator 
                    analysisData={analysisData}
                    onBackToAnalysis={handleBackToAnalysis}
                    onNewAnalysis={handleNewAnalysis}
                    existingReport={aiReport}
                    onReportGenerated={handleReportGenerated}
                  />
                </motion.div>
              )}
            </>
          )}
        </AnimatePresence>
      </main>

      <footer className="bg-white border-t border-gray-200 py-8 mt-16">
        <div className="container mx-auto px-4 text-center text-gray-600">
          <p>&copy; 2024 Paries Excel Analyzer. Analyse financière intelligente.</p>
        </div>
      </footer>
    </div>
  );
}

export default App; 